import { identity, isArray, isEmpty, isNil, isObject, map, orderBy, trim } from 'lodash';

const ARRAY_ELEMENT_WILDCARD = '*';

/**
 * Helper function that takes in a nested object and tries to sort array attributes
 * in place, without changing the shape of the original data. It uses wildcard `*`
 * to specify that the target array values should be sorted as scalars. The wildcard
 * can only appear as the last element of the attribute path.
 *
 * Examples:
 *    'items.children.weight' => will treat `children` as an array of object and try
 *                               to sort them based on the value of attribute `weight`
 *    'items.children.*'      => will sort `children` as an array of scalars
 *
 * @param dataObject
 * @param attributePath
 * @param sortOrder
 */
export function sortArrayAttribute(dataObject, attributePath, sortOrder) {
  const sort = trim(sortOrder).toLowerCase();
  const pathSegments = trim(attributePath)
    .split('.')
    .map(segment => trim(segment));

  const isInvalidPath = pathSegments.findIndex(segment => !segment) >= 0;
  const isInvalidData = isNil(dataObject) || !(isArray(dataObject) || isObject(dataObject));
  const isInvalidSort = !['asc', 'desc'].includes(sort);
  if (isInvalidPath || isInvalidData || isInvalidSort) {
    throw Error('sortArrayAttribute: missing or invalid parameters');
  }

  return sortAtPath(dataObject, '', pathSegments, sort);
}

function sortAtPath(currentData, parentPath, remainingPathSegments, sortOrder) {
  if (isEmpty(remainingPathSegments) || isNil(currentData)) return currentData;

  const currentSegment = remainingPathSegments[0];
  const currentPath = parentPath ? `${parentPath}.${currentSegment}` : currentSegment;

  if (remainingPathSegments.length === 1) {
    // We are at the last segment of the path. Try to figure out whether we need to
    // sort based on an item property value(s) or the item itself
    if (isArray(currentData)) {
      const sortIteratee = currentSegment === ARRAY_ELEMENT_WILDCARD ? identity : currentSegment;
      return orderBy(currentData, [sortIteratee], [sortOrder]);
    } else if (isObject(currentData) && isArray(currentData[currentSegment])) {
      return orderBy(currentData[currentSegment], [identity], [sortOrder]);
    } else {
      throw Error(`Wrong data at path "${currentPath}". Expected an array, got ${typeof currentData[currentSegment]}`);
    }
  } else {
    if (currentSegment === ARRAY_ELEMENT_WILDCARD) {
      throw Error(`Invalid sort expression.`);
    }

    if (isArray(currentData)) {
      return map(currentData, item => sortAtPath(item, parentPath, remainingPathSegments, sortOrder));
    } else {
      const sorted = sortAtPath(currentData[currentSegment], currentPath, remainingPathSegments.slice(1), sortOrder);
      const ret = { ...currentData };
      if (sorted !== undefined) {
        ret[currentSegment] = sorted;
      }
      return ret;
    }
  }
}
